import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@clerk/clerk-react';
import { API_BASE_URL } from '../types';

/**
 * Custom hook for client-side SKU generation.
 *
 * Initializes the SKU counter from the server and provides
 * a function to generate the next SKU.
 */
export function useSkuGeneration(prefix: string = 'ZB_') {
  const [skuCounter, setSkuCounter] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initAttempted = useRef(false);
  const { getToken } = useAuth();

  // Initialize SKU counter on mount
  useEffect(() => {
    const initializeSkuCounter = async () => {
      if (initAttempted.current) return;
      initAttempted.current = true;

      try {
        const token = await getToken({ template: 'covervision-backend' });
        if (!token) {
          setError('Authentication token not available');
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/search/sku/next-available`, {
          params: { location_prefix: prefix.replace('_', '') }, // Extract prefix without underscore
          headers: { Authorization: `Bearer ${token}` },
        });

        const nextSku = response.data.next_sku; // e.g., "ZB_0001934"
        const number = parseInt(nextSku.split('_')[1], 10);
        setSkuCounter(number);
        setIsInitialized(true);
        setError(null);

        console.log(`SKU counter initialized: ${nextSku}`);
      } catch (err) {
        console.error('Failed to initialize SKU counter:', err);
        setError('Failed to initialize SKU counter');
      }
    };

    initializeSkuCounter();
  }, [getToken, prefix]);

  /**
   * Generate the next SKU.
   * Returns null if counter is not initialized.
   */
  const getNextSku = useCallback((): string | null => {
    if (skuCounter === null) {
      console.warn('SKU counter not initialized, will use server-generated SKU');
      return null;
    }

    const numDigits = Math.max(7, skuCounter.toString().length);
    const sku = `${prefix}${skuCounter.toString().padStart(numDigits, '0')}`;

    // Increment counter for next use
    setSkuCounter(prev => (prev !== null ? prev + 1 : null));

    return sku;
  }, [skuCounter, prefix]);

  /**
   * Preview the next SKU without consuming it.
   */
  const peekNextSku = useCallback((): string | null => {
    if (skuCounter === null) return null;

    const numDigits = Math.max(7, skuCounter.toString().length);
    return `${prefix}${skuCounter.toString().padStart(numDigits, '0')}`;
  }, [skuCounter, prefix]);

  /**
   * Reset the counter to a specific value.
   * Useful for syncing with server after errors.
   */
  const resetCounter = useCallback((value: number) => {
    setSkuCounter(value);
  }, []);

  return {
    isInitialized,
    error,
    getNextSku,
    peekNextSku,
    resetCounter,
    currentCounter: skuCounter,
  };
}
